import { inject, Injectable } from "@angular/core";
import { DataRepositoryService } from "../../DataRepository/data-repository.service";
import { ReferralConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";

const REFERRAL_CONFIG = process.env.REFERRAL as unknown as ReferralConfigType;

/** A referral credit claimed server-side, applied then acked by id. */
export interface PendingReferralCredit {
  /** Stable id used to ack the credit once its deposit lands. */
  id: string;
  amount: number;
  timestamp: string;
  /** 'referee_signup' (joining bonus) | 'referrer_award' | null (legacy item). */
  reason: "referee_signup" | "referrer_award" | null;
}

interface PendingReferralCode {
  code: string;
  email: string;
}

@Injectable({
  providedIn: "root",
})
export class ReferralApi {
  private static readonly PENDING_REFERRAL_KEY = "pendingReferralCode";

  private dataRepositoryService = inject(DataRepositoryService);

  private get glossApiUrl(): string {
    const stage = REFERRAL_CONFIG.apiStage ? `/${REFERRAL_CONFIG.apiStage}` : "";
    return `${REFERRAL_CONFIG.url}${stage}`;
  }

  async getReferralCode(): Promise<{ referralCode: string; referralCount: number }> {
    const response = await this.dataRepositoryService.send(
      "GET",
      REFERRAL_CONFIG.endpoint.get_code,
      null,
      true,
      true,
      this.glossApiUrl,
    );
    return response as { referralCode: string; referralCount: number };
  }

  async applyReferralCode(referralCode: string): Promise<{ credited: boolean; amount?: number }> {
    const response = await this.dataRepositoryService.send(
      "POST",
      REFERRAL_CONFIG.endpoint.apply,
      { referralCode },
      true,
      true,
      this.glossApiUrl,
    );
    return response as { credited: boolean; amount?: number };
  }

  /**
   * Claims server-queued referral credits for delivery (non-destructive — the
   * server moves them to a "claimed" holding list). Each returned credit must
   * be acked via {@link ackPendingCredit} once its deposit lands, else it is
   * re-delivered on the next claim. This is what prevents the old loss-on-
   * failure bug where a failed deposit silently discarded the credit.
   */
  async getPendingCredits(): Promise<PendingReferralCredit[]> {
    const response = await this.dataRepositoryService.send(
      "GET",
      REFERRAL_CONFIG.endpoint.pending_credits,
      null,
      true,
      true,
      this.glossApiUrl,
    );
    return (response as { items: PendingReferralCredit[] }).items ?? [];
  }

  /** Removes a claimed credit server-side after its deposit has been confirmed. */
  async ackPendingCredit(id: string): Promise<void> {
    await this.dataRepositoryService.send(
      "DELETE",
      `${REFERRAL_CONFIG.endpoint.pending_credits}/${encodeURIComponent(id)}`,
      null,
      true,
      true,
      this.glossApiUrl,
    );
  }

  storePendingReferralCode(code: string, email: string): void {
    const normalized = this.normalizePendingReferral(code, email);
    if (!normalized) {
      return;
    }
    localStorage.setItem(ReferralApi.PENDING_REFERRAL_KEY, JSON.stringify(normalized));
  }

  /**
   * Applies the referral code captured at signup only for the matching account.
   * Any successful server response is definitive; rejected requests stay queued.
   */
  async applyPending(currentEmail?: string): Promise<void> {
    const pending = this.readPendingReferralCode();
    if (!pending) {
      return;
    }

    const normalizedEmail = this.normalizeEmail(currentEmail);
    if (pending.email && !normalizedEmail) {
      return;
    }
    if (pending.email && pending.email !== normalizedEmail) {
      localStorage.removeItem(ReferralApi.PENDING_REFERRAL_KEY);
      return;
    }

    await this.applyReferralCode(pending.code);
    localStorage.removeItem(ReferralApi.PENDING_REFERRAL_KEY);
  }

  async validateReferralCode(code: string): Promise<boolean> {
    const response = await this.dataRepositoryService.send(
      "GET",
      `${REFERRAL_CONFIG.endpoint.validate}/${encodeURIComponent(code)}`,
      null,
      false,
      true,
      this.glossApiUrl,
    );
    return (response as { valid: boolean })?.valid === true;
  }

  private readPendingReferralCode(): PendingReferralCode | null {
    const raw = localStorage.getItem(ReferralApi.PENDING_REFERRAL_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<PendingReferralCode>;
      return this.normalizePendingReferral(parsed.code, parsed.email);
    } catch {
      // Legacy string value from earlier builds. It is not account-bound, so do
      // not apply it to the next arbitrary login.
      localStorage.removeItem(ReferralApi.PENDING_REFERRAL_KEY);
      return null;
    }
  }

  private normalizePendingReferral(code?: string, email?: string): PendingReferralCode | null {
    const normalizedCode = typeof code === "string" ? code.trim() : "";
    const normalizedEmail = this.normalizeEmail(email);
    if (!normalizedCode || !normalizedEmail) {
      return null;
    }
    return { code: normalizedCode, email: normalizedEmail };
  }

  private normalizeEmail(email?: string): string {
    return typeof email === "string" ? email.trim().toLowerCase() : "";
  }
}
